import { object, string, array } from "yup";

const normalizeCargoTypeStr = (v: unknown) =>
  String(v ?? "")
    .trim()
    .toUpperCase();

/** Walk Yup `from` chain to find the parent container detail row in `containerDetailsListField`. */
const getContainerDetailFromTestContext = (ctx: { from?: { value?: unknown }[] }) => {
  const from = ctx.from || [];
  for (let i = 0; i < from.length; i++) {
    const v = from[i]?.value as Record<string, unknown> | undefined;
    if (
      v &&
      typeof v === "object" &&
      "cargoType" in v &&
      ("containerNumber" in v || "containerIndexesList" in v)
    ) {
      return v as { cargoType?: unknown };
    }
  }
  return undefined;
};

const getContainerDetailsBaseSchema = (terminalType: number) =>
  object().shape({
    entryDate:
      terminalType === 4
        ? string().required("This field is required.")
        : string().nullable().notRequired(),
    containerNumber: string(),
    containerSize: terminalType === 4 ? string() : string(),
    containerLoadType: terminalType === 4 ? string() : string(),
    cargoType: string().when("containerLoadType", {
      is: (val: string) => val === "CY",
      then: (schema) => schema.required("This field is required."),
      otherwise: (schema) => schema,
    }),
    consigneeId: string()
      .nullable()
      .when("containerLoadType", {
        is: (val: string) => val === "CY",
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    roNumber: string().max(50, "Maximum 50 characters are allowed"),
    movement: string().max(50, "Maximum 50 characters are allowed"),
    costCenter: string().max(50, "Maximum 50 characters are allowed"),
    activity: string().max(50, "Maximum 50 characters are allowed"),
    line: string().max(50, "Maximum 50 characters are allowed"),
    subLine: string().max(50, "Maximum 50 characters are allowed"),
    client: string().max(50, "Maximum 50 characters are allowed"),
    cashClient: string().max(50, "Maximum 50 characters are allowed"),
    bookingNumber: string().max(50, "Maximum 50 characters are allowed"),
    shipper: string().max(50, "Maximum 50 characters are allowed"),
    consignee: string().max(50, "Maximum 50 characters are allowed"),
    transporter: string().max(50, "Maximum 50 characters are allowed"),
    forwarder: string().max(50, "Maximum 50 characters are allowed"),
    cfAgent: string().max(50, "Maximum 50 characters are allowed"),
    previousLocation: string().max(50, "Maximum 50 characters are allowed"),
    destination: string().max(50, "Maximum 50 characters are allowed"),
    docRefNumber: string().max(50, "Maximum 50 characters are allowed"),
    dischargePort: string().max(50, "Maximum 50 characters are allowed"),
    pol: string().max(50, "Maximum 50 characters are allowed"),
    shift: string().max(50, "Maximum 50 characters are allowed"),
    scacCode: string().max(50, "Maximum 50 characters are allowed"),
    chargesStatus: string().max(50, "Maximum 50 characters are allowed"),
    eirRemarks: string().max(3000, "Maximum 3000 characters are allowed"),
    previousRemarks: string().max(3000, "Maximum 3000 characters are allowed"),
    noOfIndexes: string(),
    containerIndexesList: array().of(
      object().shape({
        indexNo: string().test(
          "index-no-loose-cargo-row",
          "This field is required.",
          function (value) {
            if (terminalType !== 4) return true;
            const container = getContainerDetailFromTestContext(
              this as unknown as { from?: { value?: unknown }[] }
            );
            const isLoose = normalizeCargoTypeStr(container?.cargoType) === "LOOSE CARGO";
            if (!isLoose) return true;
            return value != null && String(value).trim() !== "";
          }
        ),
        importerId: string(),
        indexWeight: string(),
      })
    ),
  });

export const getValidationSchema = (terminalType: number) =>
  object({
    // locationInTerminal: string().required("This field is required"),
    // vehicleStatusId: string().required("This field is required"),
    dateTime: string().required("This field is required"),
    // For terminal type 4, the UI submits `dateTime` and explicitly nulls `entryDate`.
    // Keep `entryDate` optional at the top-level to avoid hidden-field validation issues.
    entryDate: terminalType === 4 ? string().nullable().notRequired() : string(),
    driverName: string()
      .required("This field is required")
      .max(50, "Maximum 50 characters allowed")
      .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed for this field"),
    driverFatherName: string()
      .max(50, "Maximum 50 characters allowed")
      .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed for this field "),

    exporterName: string().max(50, "Maximum 50 characters allowed"),

    importerName: string().max(50, "Maximum 50 characters allowed"),
    supplierName: string()
      .max(50, "Maximum 50 characters allowed")
      .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed for this field "),
    agentName: string()
      .max(50, "Maximum 50 characters allowed")
      .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed for this field "),
    vehicleRegNumber:
      terminalType === 4
        ? string()
            .max(15, "Maximum 15 characters allowed")
            .matches(/^[a-zA-Z0-9 ]*$/, "Only alphabets are allowed for this field ")
            .when("consignmentTypeName", {
              is: (value: any) => {
                const type = typeof value === "string" ? value.toLowerCase() : "";
                return type !== "import";
              },
              then: (schema) => schema.required("This field is required"),
              otherwise: (schema) => schema,
            })
        : string()
            .required("This field is required")
            .max(15, "Maximum 15 characters allowed")
            .matches(/^[a-zA-Z0-9 ]*$/, "Only alphabets are allowed for this field "),
    vehicleTypeId: string().required("This field is required"),
    // travellingDocNumber: string()
    //   .required("This field is required")
    //   .min(1, "Minimum 1 characters are required")
    //   .max(50, "Maximum 50 characters are allowed"),
    webBocId: string().max(50, "Maximum 50 characters allowed"),
    consignmentTypeId: string().required("This field is required"),
    commercial: string().required("This field is required"),
    gdNo: string().when("consignmentTypeName", {
      is: (value: any) => {
        if (value == "Export" || value == "export") {
          return true;
        }
      },
      then: string().required("This field is required."),
      otherwise: string(),
    }),
    virNo: string().when("consignmentTypeName", {
      is: (value: any) => {
        if (value == "Import" || value == "import") {
          return true;
        }
      },
      then: string().required("This field is required."),
      otherwise: string(),
    }),
    // billNo: string().when("consignmentTypeName", {
    //   is: (value: any) => {
    //     if (value == "Import" || value == "import") {
    //       return true;
    //     }
    //   },
    //   then: string().required("This field is required."),
    //   otherwise: string(),
    // }),
    cargoTypeIds: string().when("consignmentTypeName", {
      is: (value: any) => {
        if (value != "Import" || value != "import" || value != "Export" || value != "export") {
          return false;
        }
      },
      then: string().required("This field is required."),
      otherwise: string(),
    }),
    vehicleColor: string().max(50, "Maximum 50 characters are allowed"),
    driverMobileNumber:
      terminalType === 4
        ? string()
            .max(20, "Maximum 12 characters allowed")
            .notRequired()
            .nullable()
            .test("len", "Invalid Phone Number No.", (val = "") => {
              const val_length_without_dashes = (val ?? "").replace(/-|_/g, "").length;
              return val_length_without_dashes === 13 || val_length_without_dashes === 0;
            })
        : string()
            .required("This field is required")
            .max(20, "Maximum 12 characters allowed")
            .min(1, "min 1 characters allowed")
            .test("len", "Invalid Phone Number No.", (val = "") => {
              const val_length_without_dashes = val.replace(/-|_/g, "").length;
              return val_length_without_dashes === 13 || val_length_without_dashes === 0;
            }),
    agentMobileNumber: string()
      .max(20, "Maximum 12 characters allowed")
      .min(1, "min 1 characters allowed")
      .test("len", "Invalid Phone Number No.", (val = "") => {
        const val_length_without_dashes = val.replace(/-|_/g, "").length;
        return val_length_without_dashes === 13 || val_length_without_dashes === 0;
      }),
    importerPhoneNumber: string()
      .max(20, "Maximum 12 characters allowed")
      .min(1, "min 1 characters allowed")
      .test("len", "Invalid Phone Number No.", (val = "") => {
        const val_length_without_dashes = val.replace(/-|_/g, "").length;
        return val_length_without_dashes === 13 || val_length_without_dashes === 0;
      }),
    exporterPhoneNumber: string()
      .max(20, "Maximum 12 characters allowed")
      .min(1, "min 1 characters allowed")
      .test("len", "Invalid Phone Number No.", (val = "") => {
        const val_length_without_dashes = val.replace(/-|_/g, "").length;
        return val_length_without_dashes === 13 || val_length_without_dashes === 0;
      }),
    vehicleMake: string()
      .max(4, "Maximum 4 characters allowed")
      .min(1, "min 1 characters allowed")
      .test("len", "Invalid Year", (val = "") => {
        const val_length_without_dashes = val.replace(/-|_/g, "").length;
        return val_length_without_dashes === 4 || val_length_without_dashes === 0;
      }),
    // slipNumber: string()
    //   .required("This field is required")
    //   .min(1, "Minimum 1 characters are required")
    //   .max(50, "Maximum 50 characters are allowed"),
    // travellingDocType: string().required("This field is required"),
    crmNumber: string().max(50, "Maximum 50 characters are allowed"),
    eFormNumber: string().max(50, "Maximum 50 characters are allowed"),
    passNumber: string().max(50, "Maximum 50 characters are allowed"),
    vehicleChassisNumber: string().max(50, "Maximum 50 characters are allowed"),
    vehicleBrand: string()
      .max(50, "Maximum 50 characters are allowed")
      .matches(/^[a-zA-Z0-9- ]*$/, "Only alphabets are allowed for this field "),
    vehicleModel: string().max(50, "Maximum 50 characters are allowed"),
    driverNTN: string()
      .max(50, "Maximum 50 characters are allowed")
      .matches(/^[a-zA-Z0-9- ]*$/, "Only alphanumeric are allowed for this field "),
    driverCNIC:
      terminalType === 4
        ? string()
            .max(25, "Maximum 25 characters are allowed")
            .matches(/^[A-Za-z0-9-,_ ]*$/, "Only alphanumeric are allowed for this field ")
        : string()
            .required("This field is required")
            .max(25, "Maximum 25 characters are allowed")
            .matches(/^[A-Za-z0-9-,_ ]*$/, "Only alphanumeric are allowed for this field "),
    driverResident: string().max(50, "Maximum 50 characters are allowed"),
    remarks: string().max(3000, "Maximum 3000 characters are allowed"),
    notes: string().max(3000, "Maximum 3000 characters are allowed"),
    /** Top-level index: hidden for terminal 4 when any container is LOOSE CARGO (indexes captured per row). */
    indexNo: string().test(
      "index-no-terminal4-non-loose",
      "This field is required.",
      function (value) {
        if (terminalType !== 4) return true;
        const containers = (this.parent as { containerDetailsListField?: unknown[] })
          ?.containerDetailsListField;
        const isLoose =
          Array.isArray(containers) &&
          containers.some((c: any) => normalizeCargoTypeStr(c?.cargoType) === "LOOSE CARGO");
        if (isLoose) return true;
        return value != null && String(value).trim() !== "";
      }
    ),
    containerSize:
      terminalType === 4
        ? string().when("consignmentTypeName", {
            is: (value: any) => {
              const type = typeof value === "string" ? value.toLowerCase() : "";
              return type === "import" || type === "export";
            },
            then: (schema) => schema.required("This field is required"),
            otherwise: (schema) => schema,
          })
        : string(),
    containerDetailsListField: array()
      .of(getContainerDetailsBaseSchema(terminalType))
      .when("consignmentTypeName", (consignmentTypeName, schema) => {
        const type =
          typeof consignmentTypeName === "string" ? consignmentTypeName.toLowerCase() : "";
        const baseSchema = getContainerDetailsBaseSchema(terminalType);

        if (type === "import") {
          return schema.of(
            baseSchema.shape({
              entryDate:
                terminalType === 4
                  ? string().required("This field is required.")
                  : string().nullable().notRequired(),
              containerNumber: string().required("This field is required."),
              containerSize:
                terminalType === 4 ? string() : string().required("This field is required."),
              containerLoadType:
                terminalType === 4 ? string() : string().required("This field is required."),
            })
          );
        }

        if (type === "export") {
          return schema.of(
            baseSchema.shape({
              entryDate:
                terminalType === 4
                  ? string().required("This field is required.")
                  : string().nullable().notRequired(),
              containerNumber: string().required("This field is required."),
              containerSize:
                terminalType === 4 ? string() : string().required("This field is required."),
              containerLoadType:
                terminalType === 4 ? string() : string().required("This field is required."),
            })
          );
        }

        return schema;
      }),
  });
